/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getUserDesktopItems } from '@/layout/user/user-nav';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

const BASE_PATH = '/app';

/**
 * Whether the given user-settings segment is the one currently open.
 *
 * @param pathname - Current router pathname.
 * @param segment - User-settings route segment (e.g. `profile`).
 * @returns `true` when the segment matches the current route.
 */
const isSegmentActive = (pathname: string, segment: string): boolean => {
  const target = `${BASE_PATH}/${segment}`;
  return pathname === target || pathname.startsWith(`${target}/`);
};

type UserSettingsTabsProps = {
  /** Horizontal scroller (mobile) instead of the vertical rail (desktop). */
  orientation: 'vertical' | 'horizontal';
};

const TAB_BASE = 'flex items-center no-underline shrink-0 transition-[background,color] duration-150';

/**
 * Tab navigation for the user-settings modal. Renders the same registry the
 * former sidebar used, as a vertical rail on desktop and a horizontal
 * scroller on mobile. Navigation replaces the history entry so closing the
 * modal always returns to the screen behind it.
 *
 * @param props.orientation - Layout direction of the tab list.
 * @returns The tab navigation element.
 */
const UserSettingsTabs = ({ orientation }: UserSettingsTabsProps) => {
  const { t } = useTranslation('@pages/user-settings');
  const { pathname } = useLocation();
  const items = getUserDesktopItems();
  const vertical = orientation === 'vertical';

  return (
    <nav
      data-testid="user-settings-tabs"
      aria-label={t('nav.settings')}
      className={cn(
        'flex gap-(--space-1)',
        vertical
          ? 'flex-col'
          : 'flex-row shrink-0 px-(--space-2) py-(--space-2) border-b border-(--border-subtle) overflow-x-auto',
      )}
    >
      {items.map((item) => {
        const active = isSegmentActive(pathname, item.segment);
        const label = t(item.labelKey);

        return (
          <Link
            key={item.id}
            to={`${BASE_PATH}/${item.segment}`}
            replace
            aria-current={active ? 'page' : undefined}
            data-active={active}
            className={cn(TAB_BASE, 'gap-(--space-2) rounded-(--radius-md) h-10 px-(--space-3)')}
            style={{
              background: active ? 'var(--selection-active)' : 'transparent',
              color: active ? 'var(--seed-base)' : 'var(--text-secondary)',
              fontWeight: active ? 600 : 400,
            }}
          >
            <Text component="span" className="flex items-center justify-center shrink-0">
              {item.icon}
            </Text>
            <Text
              component="span"
              className="text-(length:--text-body-sm) whitespace-nowrap"
              style={{ color: 'inherit', fontWeight: 'inherit' }}
            >
              {label}
            </Text>
          </Link>
        );
      })}
    </nav>
  );
};

export default UserSettingsTabs;
